import React, { useState, useEffect } from 'react';
import { Users, Calendar, ArrowRight, FileText } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { getCommittees } from '../services/api';

const Committees = () => {
  const [committees, setCommittees] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCommittees = async () => {
      try {
        const committeesData = await getCommittees();
        setCommittees(committeesData);
      } catch (error) {
        console.error('Error fetching committees:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCommittees();
  }, []);

  const getCommitteeColor = (index) => {
    const colors = [
      'from-yellow-400 to-yellow-600',
      'from-red-600 to-red-700',
      'from-green-600 to-green-700',
      'from-blue-600 to-blue-700',
      'from-purple-600 to-purple-700',
      'from-orange-600 to-orange-700',
    ];
    return colors[index % colors.length];
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-black border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading committees...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Section */}
      <section className="bg-gradient-to-r from-black to-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl font-bold mb-4">Parliamentary Committees</h1>
          <p className="text-xl text-gray-300">
            Specialized committees working on key policy areas for Africa's development
          </p>
        </div>
      </section>

      {/* Committees Overview */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {committees.map((committee, index) => (
              <Card key={committee.id} className="overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-1 group">
                <div className={`h-2 bg-gradient-to-r ${getCommitteeColor(index)}`}></div>
                <CardHeader>
                  <div className="flex justify-between items-start mb-2">
                    <CardTitle className="text-xl group-hover:text-yellow-600 transition-colors">
                      {committee.name}
                    </CardTitle>
                    <Badge variant="secondary" className="bg-gray-100">
                      {committee.members} Members
                    </Badge>
                  </div>
                  <CardDescription className="text-gray-600">{committee.description}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm text-gray-700">
                      <Users className="h-4 w-4 text-yellow-600" />
                      <span className="font-semibold">Chair:</span>
                      <span>{committee.chair}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-700">
                      <Calendar className="h-4 w-4 text-red-600" />
                      <span className="font-semibold">Next Meeting:</span>
                      <span>{new Date(committee.nextMeeting).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                    </div>
                  </div>
                  <div className="pt-4 border-t border-gray-200">
                    <Button variant="outline" className="w-full group-hover:bg-black group-hover:text-white transition-colors">
                      View Committee Details
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Committee Work Section */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">How Committees Work</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileText className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-gray-900">Policy Development</h3>
              <p className="text-gray-600">
                Committees draft and review legislation, conduct research, and develop policy recommendations.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-red-600 to-red-700 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-gray-900">Expert Consultations</h3>
              <p className="text-gray-600">
                Committees invite experts, stakeholders, and citizens to provide input on important issues.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-black to-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
                <Calendar className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-gray-900">Regular Meetings</h3>
              <p className="text-gray-600">
                Committees meet regularly to discuss ongoing work and make decisions on legislative matters.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Upcoming Meetings */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Upcoming Committee Meetings</h2>
          <Card>
            <CardContent className="p-0">
              <div className="divide-y divide-gray-200">
                {committees.slice(0, 4).map((committee, index) => (
                  <div key={committee.id} className="p-6 hover:bg-gray-50 transition-colors">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                      <div className="flex items-start gap-4">
                        <div className={`w-12 h-12 bg-gradient-to-br ${getCommitteeColor(index)} rounded-lg flex items-center justify-center flex-shrink-0`}>
                          <Calendar className="h-6 w-6 text-white" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900 mb-1">{committee.name}</h3>
                          <p className="text-sm text-gray-600">Chair: {committee.chair}</p>
                          <p className="text-sm text-gray-500 mt-1">
                            {new Date(committee.nextMeeting).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                          </p>
                        </div>
                      </div>
                      <Button variant="outline" className="hover:bg-black hover:text-white transition-colors">
                        View Agenda
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default Committees;